import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Upload, FileText, Eye, Trash2, Check, X, AlertTriangle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useI18n } from "@/context/I18nContext";
import { fileViewUrl } from "@/lib/files";
import { Button } from "@/components/ui/button";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";

const STATUS_STYLE = {
  pending: "bg-amber-50 text-amber-700 border border-amber-200",
  approved: "bg-emerald-50 text-emerald-700 border border-emerald-200",
  rejected: "bg-rose-50 text-rose-700 border border-rose-200",
};

export function DocumentsTab({ orderId }) {
  const { user } = useAuth();
  const { locale, t } = useI18n();
  const isStaff = ["admin", "support"].includes(user?.role);
  const [required, setRequired] = useState(null);
  const [docs, setDocs] = useState([]);
  const [category, setCategory] = useState("death_certificate");
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = async () => {
    try {
      const [{ data: req }, { data: list }] = await Promise.all([
        api.get(`/documents/required/${orderId}`),
        api.get(`/documents`, { params: { entity_type: "order", entity_id: orderId } }),
      ]);
      setRequired(req);
      setDocs(list);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  useEffect(() => { load(); }, [orderId]);

  const catName = (code) => {
    const c = required?.categories?.find((x) => x.code === code);
    return c ? (c.name?.[locale] || c.name?.it || code) : code;
  };

  const onUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    fd.append("entity_type", "order");
    fd.append("entity_id", orderId);
    fd.append("category", category);
    setUploading(true);
    try {
      await api.post("/documents/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(t("doc.uploaded"));
      if (fileRef.current) fileRef.current.value = "";
      load();
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setUploading(false); }
  };

  const review = async (id, status) => {
    try { await api.patch(`/documents/${id}/status`, { status }); toast.success(t("doc.updated")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const del = async (id) => {
    try { await api.delete(`/documents/${id}`); toast.success(t("doc.deleted")); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="documents-tab">
      {required && required.missing.length > 0 && (
        <div className="flex items-start gap-3 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3" data-testid="missing-docs-alert">
          <AlertTriangle size={18} className="text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-800">
            <span className="font-semibold">Documenti obbligatori mancanti:</span>{" "}
            {required.missing.map(catName).join(", ")}
          </div>
        </div>
      )}
      {required && required.complete && (
        <div className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800" data-testid="docs-complete-alert">
          <Check size={18} className="text-emerald-600" /> Tutti i documenti obbligatori sono presenti.
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 rounded-xl border border-slate-200 bg-slate-50 p-4">
        <div className="flex-1 min-w-[200px]">
          <label className="text-xs uppercase tracking-wider text-slate-400">Categoria</label>
          <Select value={category} onValueChange={setCategory}>
            <SelectTrigger data-testid="doc-category-select" className="bg-white mt-1"><SelectValue /></SelectTrigger>
            <SelectContent>
              {(required?.categories || []).map((c) => (
                <SelectItem key={c.code} value={c.code}>
                  {catName(c.code)}{c.required ? " *" : ""}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <input ref={fileRef} type="file" onChange={onUpload} className="hidden" data-testid="doc-file-input" />
        <Button onClick={() => fileRef.current?.click()} disabled={uploading}
          className="bg-blue-900 hover:bg-blue-800" data-testid="doc-upload-btn">
          <Upload size={16} className="mr-1.5" /> {uploading ? "Caricamento…" : "Carica documento"}
        </Button>
      </div>

      <div className="space-y-2">
        {docs.length === 0 && <p className="text-sm text-slate-400 py-6 text-center">Nessun documento caricato.</p>}
        {docs.map((d) => (
          <div key={d.id} className="flex items-center gap-3 rounded-lg border border-slate-200 bg-white p-3" data-testid="document-row">
            <FileText size={18} className="text-slate-400 shrink-0" />
            <div className="min-w-0 flex-1">
              <div className="text-sm font-medium text-slate-800 truncate">{d.original_filename}</div>
              <div className="text-xs text-slate-400">{catName(d.category)} · {(d.size / 1024).toFixed(0)} KB</div>
            </div>
            <span className={`text-xs px-2 py-0.5 rounded-full capitalize ${STATUS_STYLE[d.status] || ""}`} data-testid="doc-status-badge">{d.status}</span>
            <a href={fileViewUrl(d.id)} target="_blank" rel="noreferrer" className="text-slate-500 hover:text-blue-800 p-1" data-testid="doc-view-link"><Eye size={16} /></a>
            {isStaff && d.status !== "approved" && (
              <button onClick={() => review(d.id, "approved")} className="text-emerald-600 hover:text-emerald-800 p-1" title="Approva" data-testid="doc-approve-btn"><Check size={16} /></button>
            )}
            {isStaff && d.status !== "rejected" && (
              <button onClick={() => review(d.id, "rejected")} className="text-rose-500 hover:text-rose-700 p-1" title="Rifiuta" data-testid="doc-reject-btn"><X size={16} /></button>
            )}
            {(isStaff || d.uploaded_by === user?.id) && (
              <button onClick={() => del(d.id)} className="text-slate-400 hover:text-rose-600 p-1" title="Elimina" data-testid="doc-delete-btn"><Trash2 size={16} /></button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
